import { transcriptSchema, type Transcript } from '@fjalim/shared'
import { DEVICE_HEADER, getDeviceId, rememberDeviceId } from '../lib/device'
import { apiUrl, rawRequest, request } from './client'
import type {
  Account,
  ApiKey,
  CreatedApiKey,
  ExportFormat,
  LedgerEntry,
  Locale,
  Page,
  RenderFraming,
  RenderJob,
  RenderQuote,
  Transcription,
  TranscriptionOptions,
  Usage,
} from './types'

export * from './client'
export * from './types'
export { uploadFile, type UploadProgress } from './upload'

/* ── auth (session) ─────────────────────────────────────────────────── */

export const auth = {
  me: () => request<{ account: Account }>('/auth/me').then((r) => r.account),
  /** Always 201, also for an existing email; follow with `me()` to learn whether a session exists. */
  signup: async (input: { email: string; password: string; name?: string; locale?: Locale; turnstile_token?: string }) => {
    // The first-party device id (cookie, localStorage and IndexedDB), for the per-device cap on signup grants.
    const device = await getDeviceId()
    const res = await request<{ device_token?: string } | null>('/auth/signup', { method: 'POST', body: input, headers: { [DEVICE_HEADER]: device } })
    // The API's own cookie may have won over ours; keep the cookie, localStorage and IndexedDB on the id it accepted.
    await rememberDeviceId(res?.device_token)
  },
  login: (input: { email: string; password: string }) =>
    request<{ account: Account }>('/auth/login', { method: 'POST', body: input }).then((r) => r.account),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  verify: (token: string) =>
    request<{ verified: boolean; signup_grant: boolean }>('/auth/verify', { method: 'POST', body: { token } }),
  resend: () => request<{ sent: boolean; already_verified: boolean }>('/auth/resend', { method: 'POST' }),
  forgot: (email: string) => request<{ sent: boolean }>('/auth/password/forgot', { method: 'POST', body: { email } }),
  reset: (token: string, password: string) =>
    request<{ reset: boolean }>('/auth/password/reset', { method: 'POST', body: { token, password } }),
}

/* ── transcriptions ─────────────────────────────────────────────────── */

export const transcriptions = {
  list: (cursor?: string, signal?: AbortSignal) =>
    request<Page<Transcription>>(apiUrlPath('/v1/transcriptions', { limit: 50, cursor }), { signal }),
  get: (id: string, signal?: AbortSignal) => request<Transcription>(`/v1/transcriptions/${id}`, { signal }),
  /** Omit `options` when nothing was asked for: the API stores `{}` either way (spec §12). */
  create: (uploadId: string, options?: TranscriptionOptions) =>
    request<Transcription>('/v1/transcriptions', {
      method: 'POST',
      body: { upload_id: uploadId, ...(options ? { options } : {}) },
    }),
  remove: (id: string) => request<void>(`/v1/transcriptions/${id}`, { method: 'DELETE' }),
  transcript: async (id: string, signal?: AbortSignal): Promise<Transcript> => {
    const raw = await request<unknown>(`/v1/transcriptions/${id}/transcript`, { signal })
    return transcriptSchema.parse(raw)
  },
  /** Saves a new version. `ifMatch` is the version this edit was based on. */
  saveTranscript: (id: string, transcript: Transcript, ifMatch: number) =>
    request<Transcription>(`/v1/transcriptions/${id}/transcript`, {
      method: 'PUT',
      body: transcript,
      headers: { 'If-Match': String(ifMatch) },
    }),
  audioUrl: (id: string) => apiUrl(`/v1/transcriptions/${id}/audio`),
}

/* ── renders (burned-in caption videos, spec §11) ───────────────────── */

export interface CreateRenderInput {
  preset: string
  style: unknown
  framing: RenderFraming
}

export const renders = {
  list: (transcriptionId: string, signal?: AbortSignal) =>
    request<{ data: RenderJob[] }>(`/v1/transcriptions/${transcriptionId}/renders`, { signal }).then((r) => r.data),
  create: (transcriptionId: string, input: CreateRenderInput) =>
    request<RenderJob>(`/v1/transcriptions/${transcriptionId}/renders`, { method: 'POST', body: input }),
  remove: (id: string) => request<void>(`/v1/renders/${id}`, { method: 'DELETE' }),
  quote: (transcriptionId: string, signal?: AbortSignal) =>
    request<RenderQuote>(apiUrlPath('/v1/renders/quote', { transcription_id: transcriptionId }), { signal }),
  /** The API answers with a redirect to a presigned download, so the browser follows this URL itself. */
  downloadUrl: (id: string) => apiUrl(`/v1/renders/${id}/download`),
}

/** Path plus query, for `request()` which prefixes the base URL itself. */
function apiUrlPath(path: string, query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) if (v !== undefined) params.set(k, String(v))
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

/** Query string for GET …/transcript?format= (spec §7). */
export function exportQuery(format: ExportFormat, preset?: string): Record<string, string> {
  switch (format) {
    case 'txt':
      return { format: 'txt' }
    case 'txt-timestamps':
      return { format: 'txt', timestamps: '1' }
    case 'srt':
    case 'vtt':
      // The caption preset ("Për": YouTube, TikTok, …) shapes the cues on the server.
      return preset ? { format, preset } : { format }
    default:
      return { format }
  }
}

export function exportUrl(id: string, format: ExportFormat, preset?: string): string {
  return apiUrl(`/v1/transcriptions/${id}/transcript`, exportQuery(format, preset))
}

export const EXPORT_EXTENSION: Record<ExportFormat, string> = {
  txt: 'txt',
  'txt-timestamps': 'txt',
  srt: 'srt',
  vtt: 'vtt',
  json: 'json',
}

export const EXPORT_MIME: Record<ExportFormat, string> = {
  txt: 'text/plain;charset=utf-8',
  'txt-timestamps': 'text/plain;charset=utf-8',
  srt: 'application/x-subrip;charset=utf-8',
  vtt: 'text/vtt;charset=utf-8',
  json: 'application/json',
}

/** Fetches an export through the API (cookies included) and returns it as a Blob. */
export async function fetchExport(id: string, format: ExportFormat, preset?: string): Promise<Blob> {
  const res = await rawRequest('', { headers: { Accept: '*/*' } }, exportUrl(id, format, preset))
  const text = await res.text()
  return new Blob([text], { type: EXPORT_MIME[format] })
}

/* ── account ────────────────────────────────────────────────────────── */

export const account = {
  balance: () => request<{ balance_micro_eur: number; currency: string }>('/v1/balance'),
  keys: () => request<{ data: ApiKey[] }>('/account/keys').then((r) => r.data),
  createKey: (name: string) => request<CreatedApiKey>('/account/keys', { method: 'POST', body: { name } }),
  revokeKey: (id: string) => request<void>(`/account/keys/${id}`, { method: 'DELETE' }),
  webhookSecret: () => request<{ webhook_secret: string }>('/account/webhook-secret').then((r) => r.webhook_secret),
  rotateWebhookSecret: () =>
    request<{ webhook_secret: string }>('/account/webhook-secret/rotate', { method: 'POST' }).then(
      (r) => r.webhook_secret,
    ),
}

/* ── billing ────────────────────────────────────────────────────────── */

export const billing = {
  checkout: (amountEur: number) =>
    request<{ url: string }>('/billing/checkout', { method: 'POST', body: { amount_eur: amountEur } }),
  ledger: (cursor?: string) => request<Page<LedgerEntry>>(apiUrlPath('/billing/ledger', { limit: 50, cursor })),
  usage: (from?: string, to?: string) => request<Usage>(apiUrlPath('/v1/usage', { from, to })),
}
